/* eslint-disable no-useless-escape */

'use strict'

import {getCountry} from './esw-geo-location-helper'
import {getAbandonmentCart, getBmConfigs, getGeoIpAlert, getOrderNumber} from './esw-services'

/**
 * Get configuration value from esw.config localStorage
 * @param {string} configKey - Configuration json key in esw.configs
 * @returns {string} - Correspondent key from esw.configs localStorage
 */
export const getEswConfigByKey = (configKey) => {
    if (typeof window === 'undefined') {
        return undefined
    }

    try {
        const configs = localStorage.getItem('esw.configs')
        const parsedConfigs = configs ? JSON.parse(configs) : null

        return parsedConfigs?.[configKey]
    } catch (error) {
        return undefined
    }
}

export const getAbTastyScriptPath = () => {
    try {
        return getEswConfigByKey('abTastyScriptPath')
    } catch (e) {
        return null
    }
}

export const setAbTastyScriptInDom = (abTastyScriptPath) => {
    if (abTastyScriptPath) {
        // Remove existing script if present
        const existingScript = document.getElementById('eswAbTastyScript')
        if (existingScript) {
            document.head.removeChild(existingScript)
        }

        // Add AB Tasty script
        const abTastyScript = document.createElement('script')
        abTastyScript.src = abTastyScriptPath
        abTastyScript.async = true
        abTastyScript.id = 'eswAbTastyScript'
        document.head.appendChild(abTastyScript)
    }

    const scriptElement = document.getElementById('eswAbTastyScript')
    if (scriptElement && abTastyScriptPath.length === 0) {
        document.head.removeChild(scriptElement)
    }
}

const storeBmConfigs = (locale) => {
    // Only run on client-side
    if (typeof window === 'undefined') {
        return
    }

    getBmConfigs(locale)
        .then((response) => response.json())
        .then((data) => {
            data.eswBmConfigs.updatedAt = new Date()
            localStorage.setItem('esw.configs', JSON.stringify(data.eswBmConfigs))
            localStorage.setItem(
                'esw.shopperPricingConfigs',
                JSON.stringify(data.shopperPricingConfigs)
            )
            if (
                data.eswBmConfigs &&
                Object.prototype.hasOwnProperty.call(data.eswBmConfigs, 'eswNativeShippingEnabled')
            ) {
                localStorage.setItem(
                    'esw.eswNativeShippingEnabled',
                    JSON.stringify(data.eswBmConfigs.eswNativeShippingEnabled)
                )
            }
            // Setting AB Tasty DOM
            setAbTastyScriptInDom(data.eswBmConfigs.abTastyScriptPath)
        })
        .catch((error) => {
            console.error('Error storing BM configs:', error)
            return error
        })
}

/**
 * Get configuration value from access_token localStorage
 * @param {string} configKey - Configuration json key in esw.configs
 * @returns {string} - Correspondent key from access_token localStorage
 */
export const getEswSiteAccessTokenByKey = (configKey) =>
    localStorage.getItem('access_token_' + configKey)
/**
 * Get shopper currency config from local storage
 * @param {string} configKey - bm config key
 * @returns {Object} - currency config from local storage
 */
export const getEswShopperCurrencyConfigByKey = (configKey) => {
    try {
        const configs = localStorage.getItem('esw.shopperPricingConfigs')
        const parsedConfigs = configs ? JSON.parse(configs) : ''

        return parsedConfigs ? parsedConfigs[configKey] : ''
    } catch (error) {
        return
    }
}

export const isTrue = (value) => value === true || value === 'true'

export const getIsFixedPriceModel = () =>
    isTrue(getEswShopperCurrencyConfigByKey('isFixedPriceModel'))
/**
 * All ESW functions call on App init
 * Only runs on client-side after React loads
 * @param {string} locale - site locale en-IE etc
 */
export const eswAppInit = (locale) => {
    // Only run on client-side
    if (typeof window === 'undefined') {
        return
    }
    storeBmConfigs(locale)
}

/**
 * Call geo ip alert controller
 * @param {string} shopperCountry - shopper country
 * @param {string} bearerToken - optional bearer token for authentication
 * @returns {Promise} - fetch promise
 */
export const getGeoIpAlertInfo = (shopperCountry, bearerToken = null) => {
    // Only run on client-side
    if (typeof window === 'undefined') {
        return Promise.resolve({json: () => Promise.resolve({geoIpInfo: {}})})
    }
    return getGeoIpAlert(shopperCountry, bearerToken)
}

/**
 * Call abandonment cart controller
 * @param {string} eswClientLastOrderId - last order id
 * @param {string} locale - shopper country
 * @param {string} bearerToken - optional bearer token for authentication
 * @returns {Promise} - fetch promise
 */
export const getAbandonmentCartHelper = (eswClientLastOrderId, locale, bearerToken = null) => {
    // Only run on client-side
    if (typeof window === 'undefined') {
        return Promise.resolve({json: () => Promise.resolve({})})
    }
    return getAbandonmentCart(eswClientLastOrderId, locale, bearerToken)
}

/**
 * Call get order number controller
 * @param {string} orderNumber - orderNumber
 * @returns {Promise} - fetch promise
 */
export const getOrderNumberHelper = (orderNumber) => {
    // Only run on client-side
    if (typeof window === 'undefined') {
        return Promise.resolve({json: () => Promise.resolve({})})
    }
    return getOrderNumber(orderNumber)
}

/**
 * Get cookie by name after react loaded
 * @param {string} name - cookie name
 * @returns {string} - cookie value
 */
export const getCookie = (name) => {
    if (typeof document === 'undefined') return ''
    const escape = (s) => {
        return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1')
    }
    let match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'))
    return match ? match[1] : null
}

/**
 * Return country code from locale
 * @param {string} locale - for example en-IE, en-US format
 * @returns {string} - country code
 */
export const getLocaleCountry = (locale) => {
    let localeArr = locale.split('-')
    if (localeArr.length > 1) {
        return localeArr[1]
    }
    return locale
}

export const getShopperCountry = () => {
    return getCountry()
}

/**
 * Check if first visit to the site
 * @returns {boolean} - true if first visit
 */
export const isFirstVisit = () => {
    if (localStorage.getItem('esw.Visited')) {
        return false
    }
    localStorage.setItem('esw.Visited', 1)
    return true
}

/**
 * Get locale by country code
 * @param {string} countryCode - country code
 * @param {Object} siteConfig - site config
 * @returns {string} - locale
 */
export const getLocaleByCountry = (countryCode, siteConfig) => {
    let countryLocale = null
    if (countryCode && countryCode.length > 0) {
        let countrySiteObj = siteConfig.l10n.supportedLocales.filter((locale) => {
            return locale.countryCode === countryCode
        })
        if (countrySiteObj && countrySiteObj.length > 0) {
            countryLocale = countrySiteObj[0].id
        }
    }
    return countryLocale
}

/**
 * Get country code by locale
 * @param {string} locale - locale
 * @param {Object} siteConfig - site config
 * @returns {string} - country code
 */
export const getCountryCodeByLocale = (locale, siteConfig) => {
    if (!locale) return null
    const match = siteConfig.l10n.supportedLocales.find((l) => l.id === locale)
    return match?.countryCode ?? null
}

/**
 * Add spinner to the HTML
 * @param {string} loadingMsg - Loading message
 * @returns {Object} - spinner dom element
 */
export const generateDomLoader = (loadingMsg) => {
    let spinnerMsg = loadingMsg || getEswConfigByKey('defaultLoaderText')
    if (
        typeof spinnerMsg === 'undefined' ||
        !spinnerMsg ||
        (spinnerMsg && spinnerMsg.length === 0)
    ) {
        spinnerMsg = 'Loading...'
    }
    // Create a new div element for the backdrop
    const backdrop = document.createElement('div')
    backdrop.style.position = 'fixed'
    backdrop.style.top = '0'
    backdrop.style.right = '0'
    backdrop.style.bottom = '0'
    backdrop.style.left = '0'
    backdrop.style.background = 'rgba(0, 0, 0, 1)'
    backdrop.style.display = 'flex'
    backdrop.style.justifyContent = 'center'
    backdrop.style.alignItems = 'center'
    backdrop.style.flexDirection = 'column'
    backdrop.style.zIndex = '9999'

    // Create a new div element for the spinner
    const spinner = document.createElement('div')
    spinner.style.display = 'inline-block'
    spinner.style.width = '80px'
    spinner.style.height = '80px'
    spinner.style.border = '8px solid rgba(0, 0, 0, 0.1)'
    spinner.style.borderRadius = '50%'
    spinner.style.borderTop = '8px solid #3498db'
    spinner.style.animation = 'spin 2s linear infinite'

    // Create a new div element for the message
    const message = document.createElement('div')
    message.style.color = '#fff'
    message.style.marginTop = '20px'
    message.textContent = spinnerMsg

    // Add the spin animation
    const style = document.createElement('style')
    style.innerHTML = `
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
    `
    document.head.appendChild(style)

    // Append the spinner and message to the backdrop
    backdrop.appendChild(spinner)
    backdrop.appendChild(message)

    // Append the backdrop to the body
    document.body.appendChild(backdrop)

    // Return the backdrop so it can be removed later
    return backdrop
}

/**
 * Validates whether a redirect URL can be used for navigation.
 * @param {string} url
 * @returns {boolean}
 */
export const isValidRedirectUrl = (url) => {
    if (!url || typeof url !== 'string') {
        return false
    }
    try {
        // eslint-disable-next-line no-new
        new URL(url)
        return true
    } catch (error) {
        return false
    }
}

/**
 * Maps country selector to an object with country as the key and resolved URL for the home page as the value
 * @param {*} countrySelectorData
 * @returns {Object} - object with country as the key and resolved URL for the home page as the value
 */
export const mapCountrySelectorDataToGeoPopupRedirectPath = (countrySelectorData) => {
    const countrySelectorUrls = Object.keys(countrySelectorData.countrySelectorConfig).reduce((acc, keyname) => {
        const countryConfig = countrySelectorData.countrySelectorConfig[keyname]
        // domains may be an object with multiple domains for different brand
        // TODO: make this dynamic based on the brand when CA/US move to headless
        const domain = countryConfig.domain['garage'] || countryConfig.domain
        // ensure domain includes http or https
        const cleanDomain = domain.startsWith('http') ? domain : `https://${domain}`
        const countryCode = countryConfig.locale.split('_')[1].toLowerCase()
        return {
            ...acc,
            [countryCode]: `${cleanDomain}/${countryConfig.path}`
        }
    }, {})
    return countrySelectorUrls
}

export const removeDomLoader = (spinner) => {
    spinner.parentNode.removeChild(spinner)
}
